import { useInterval } from '@chakra-ui/react';
import { ReactNode, useEffect, useState } from 'react';
import authApi from '~/authApi';
import clientApi from '~/clientApi';
import { FEATURE_FLAGS } from '~/constants';
import { logger } from '~/core/logger/logger';
import { areEqualMajorVersions } from '~/utils';

import { Error503 } from '../Error503';
import { useFlagStore } from '../FeatureFlagControls/store';
import {
  MAINTENANCE_MODE_EVENT,
  POLL_INTERVAL,
  UNAVAILABLE_RESOURCE_CODE,
} from './MaintenanceModeInterceptor.constants';
import {
  Status,
  StatusAPIResponseDTO,
} from './MaintenanceModeInterceptor.model';

export interface IMaintenanceModeInterceptorProps {
  children?: ReactNode;
}

export const MaintenanceModeInterceptor: React.FC<
  IMaintenanceModeInterceptorProps
> = ({ children }) => {
  const [isUnderMaintenanceMode, setMaintenanceMode] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const { flags, status: flagStoreStatus } = useFlagStore();
  const isMaintenanceFlagEnabled =
    flags[FEATURE_FLAGS.foamForceMaintenance]?.value;

  const checkStatus = async () => {
    const { status, releaseId } =
      (await clientApi.status.index()) as StatusAPIResponseDTO;
    const gitTag = process.env.NEXT_PUBLIC_GIT_TAG as string;

    if (status === Status.ok && areEqualMajorVersions(gitTag, releaseId)) {
      setMaintenanceMode(false);

      window.location.reload();
    } else {
      // Log when maintenance mode is active due to version mismatch
      if (status !== Status.ok || !areEqualMajorVersions(gitTag, releaseId)) {
        logger
          .withMetadata({
            section: 'maintenance-mode',
            reason: 'version_mismatch',
            apiStatus: status,
            gitTag,
            releaseId,
            areVersionsEqual: areEqualMajorVersions(gitTag, releaseId),
          })
          .warn('Maintenance mode active due to version mismatch');
      }
    }
  };

  const handleMaintenanceMode = (
    eventData: CustomEvent<{ isUnderMaintenance: boolean }>,
  ) => {
    if (eventData.detail.isUnderMaintenance) {
      setMaintenanceMode(true);
      logger
        .withMetadata({
          section: 'maintenance-mode',
          isUnderMaintenance: true,
        })
        .warn('Maintenance mode activated via custom event');
    } else {
      // Check if error is displayed to avoid multiple renders
      if (isUnderMaintenanceMode) {
        setMaintenanceMode(false);

        window.location.reload();
      }
    }
  };

  const error503Interceptor = (error: any) => {
    const is503 = error?.response?.status === UNAVAILABLE_RESOURCE_CODE;

    // Check not under maintenance to avoid multiple renders
    if (is503 && !isUnderMaintenanceMode) {
      logger
        .withMetadata({
          section: 'maintenance-mode',
          errorStatus: error?.response?.status,
          errorMessage: error?.response?.data?.message || 'No error message',
        })
        .warn('Maintenance mode activated via 503 error');
      return setMaintenanceMode(true);
    }

    return Promise.reject(error);
  };

  useEffect(() => {
    // Set isClient to true on mount (only runs on client, not during SSR)
    setIsClient(true);

    // Set up event listeners and interceptors
    window.addEventListener(MAINTENANCE_MODE_EVENT, handleMaintenanceMode);

    clientApi._apiInstance.interceptors.response.use(
      undefined,
      error503Interceptor,
    );

    authApi._apiInstance.interceptors.response.use(
      undefined,
      error503Interceptor,
    );

    return () => {
      window.removeEventListener(MAINTENANCE_MODE_EVENT, handleMaintenanceMode);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInterval(checkStatus, isUnderMaintenanceMode ? POLL_INTERVAL : null);

  // During SSR, always render children to allow NextSeo to inject meta tags
  if (!isClient) {
    return <>{children}</>;
  }

  // Client-side only: check maintenance mode status
  if (flagStoreStatus === 'loading') {
    // Return children during loading to prevent blank screen and allow meta tags
    return <>{children}</>;
  }

  // Show 503 if maintenance flag is enabled, feature flag system errored, or all flags are false
  if (isMaintenanceFlagEnabled || flagStoreStatus === 'error') {
    if (isMaintenanceFlagEnabled) {
      logger
        .withMetadata({
          section: 'maintenance-mode',
          flagStoreStatus,
        })
        .warn('Maintenance mode activated via force maintenance flag');
    } else if (flagStoreStatus === 'error') {
      logger
        .withMetadata({
          section: 'maintenance-mode',
          flagStoreStatus,
        })
        .warn('Maintenance mode activated due to feature flag system error');
    }

    return <Error503 />;
  }

  return <>{isUnderMaintenanceMode ? <Error503 /> : children} </>;
};
